import { FC, useCallback, useEffect, useRef, useState } from 'react'
// import Modal from '@common/Modal'
import { Modal } from 'antd'
import cn from 'classnames'
import PageContent, {
  PageContentRef,
} from '@pages/Admin/ManageClassSettings/components/PageContent/PageContent'
import { useAsyncRetry } from 'react-use'
import { classApi, directionAPI, directionGroupApi } from '@/api'
import { ClassInfoType } from '@pages/Admin/ManageClassSettings/types'
import {
  GeneralCourseInformation,
  RefetchGeneralDataVariantType,
} from '@pages/Admin/ManageClassSettings'
import { useAddAlert } from '@/hooks/useAddAlert'
import { selectProfile } from '@store/profile.reducer'
import { useSelector } from 'react-redux'
import { IcoRemove } from '@pages/Admin/constsIcon'
import Button from '@UI/New/Button/Button'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import classes from './index.module.css'

interface EditClassesModalProps {
  classId?: number
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const EditClassesModal: FC<EditClassesModalProps> = ({
  classId,
  open,
  onClose,
  onSuccess = () => {},
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const pageContentRef = useRef<PageContentRef>(null)

  const [unsavedChanges, setUnsavedChanges] = useState(false)

  const classInfoState = useAsyncRetry(
    async () => await classApi.show(classId),
    [classId],
  )
  const foldersState = useAsyncRetry(async () => await classApi.folders(), [])
  const directionsState = useAsyncRetry(
    async () =>
      await directionAPI.getAll({
        filter:
          profile.role === 'senior-seminarian'
            ? [`[direction_id][in][]=${profile.directionIds}`]
            : [],
        expand: ['directionTariffs'],
      }),
    [profile.role, profile.directionIds],
  )
  const groupsState = useAsyncRetry(
    async () => await directionGroupApi.getList({ expand: ['teacher'] }),
    [],
  )

  const classInfoData = classInfoState.value?.data?.data as ClassInfoType
  const allFoldersData =
    (foldersState.value?.data.data as GeneralCourseInformation['allFolders']) ??
    []
  const allDirectionsData =
    (directionsState.value?.data
      .data as GeneralCourseInformation['allDirections']) ?? []
  const allGroupsData =
    (groupsState.value?.data as GeneralCourseInformation['allGroups']) ?? []

  useEffect(() => {
    if (classInfoState.error)
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось получить информацию о классе',
        type: 'error',
      })
  }, [classInfoState.error])

  useEffect(() => {
    if (foldersState.error || directionsState.error || groupsState.error)
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось получить данные',
        type: 'error',
      })
  }, [foldersState.error, directionsState.error, groupsState.error])

  const refetchByType = useCallback(
    (type: RefetchGeneralDataVariantType) => {
      switch (type) {
        case 'class':
          classInfoState.retry()
          break
        case 'all-general':
          foldersState.retry()
          directionsState.retry()
          groupsState.retry()
          break
        case 'folders':
          foldersState.retry()
          break
        case 'directions':
          directionsState.retry()
          break
        case 'groups':
          groupsState.retry()
          break
        default:
          break
      }
    },
    [
      foldersState.retry,
      directionsState.retry,
      groupsState.retry,
      classInfoState.retry,
    ],
  )

  return (
    // <Modal
    //   active={open}
    //   className={cn(mclasses.modalWrapper) as any}
    //   setActive={() => onClose()}
    // >
    <Modal
      open={open}
      onOk={() => onClose()}
      onCancel={() => onClose()}
      footer={null}
      title={null}
      centered
      maskClosable
      className={cn(amclasses.modal, classes.modal)}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Редактирование класса
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
        <div className={classes.pageContentWrapper}>
          <PageContent
            ref={pageContentRef}
            classInfo={classInfoData}
            generalData={{
              allFolders: allFoldersData,
              allDirections: allDirectionsData,
              allGroups: allGroupsData,
            }}
            refetchGeneralData={refetchByType}
            unsavedChanges={unsavedChanges}
            setUnsavedChanges={setUnsavedChanges}
            hiddenParts={{ settings: true }}
          />
        </div>
        <div className={mclasses.actions}>
          <Button color="gray" onClick={() => onClose()}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={async () => {
              await pageContentRef.current?.handleSaveClass({
                onSuccessCallback: () => {
                  setUnsavedChanges(false)
                  onSuccess()
                }
              })
            }}
            disabled={!unsavedChanges}
            isLoading={pageContentRef.current?.isSaving}
          >
            Сохранить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default EditClassesModal
